import React, { useState, useEffect } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useContent } from '../hooks/useContent';

export const ContactSection = () => {
  const { content, loading } = useContent();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: '',
    region: '',
    services: '',
    message: ''
  });

  // Get pre-selected service from URL parameters
  useEffect(() => {
    if (!content) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const preSelectedService = urlParams.get('service');
    if (preSelectedService && content.contact.serviceOptions.includes(preSelectedService)) {
      setFormData(prev => ({
        ...prev,
        services: preSelectedService
      }));
    }
  }, [content]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading || !content) {
    return (
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-navy-900 text-xl">Loading contact form...</div>
        </div>
      </section>
    );
  }

  const { contact } = content;

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-navy-900 mb-4">
            {contact.sectionTitle.split(' ')[0]} {contact.sectionTitle.split(' ')[1]} <span className="text-blue-600">{contact.sectionTitle.split(' ').slice(2).join(' ')}</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {contact.sectionDescription}
          </p>
        </div>

        {/* Contact Form */}
        <Card className="shadow-xl border-0 max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle className="text-2xl text-navy-900">{contact.formTitle}</CardTitle>
            <CardDescription>
              {contact.formDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form 
              action={contact.formspreeEndpoint}
              method="POST"
              className="space-y-6"
            >
              {/* Hidden redirect field */}
              <input type="hidden" name="_redirect" value={window.location.origin + "/thanks"} />
              
              {/* Honeypot field */}
              <input type="text" name="_gotcha" style={{display: 'none'}} />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{contact.fields.name} *</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{contact.fields.email} *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">{contact.fields.phone} *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="company">{contact.fields.company}</Label>
                  <Input
                    id="company"
                    name="company"
                    type="text"
                    value={formData.company}
                    onChange={(e) => handleInputChange('company', e.target.value)}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="services">{contact.fields.services}</Label>
                <Select 
                  value={formData.services}
                  onValueChange={(value) => handleInputChange('services', value)}
                  name="services"
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select service of interest" />
                  </SelectTrigger>
                  <SelectContent>
                    {contact.serviceOptions.map((service) => (
                      <SelectItem key={service} value={service}>{service}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Hidden input to ensure form submission includes the selected value */}
                <input type="hidden" name="services" value={formData.services} />
              </div>

              <div>
                <Label htmlFor="message">{contact.fields.message} *</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  required
                  placeholder="Tell us about your compliance challenges and goals..."
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="mt-1"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg font-semibold"
              >
                {contact.submitText}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};